import React from 'react';
import { Droplet, DollarSign, Waves, Ruler, FileText, ShowerHead, Wind, Thermometer } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета объема бассейна
 */
const ResultsOverview = ({ results, formData, poolTypes }) => {
  if (!results) return null;

  // Форматирование имени типа бассейна
  const formatPoolTypeName = (type) => {
    const poolType = poolTypes.find(pt => pt.id === type);
    return poolType ? poolType.name.toLowerCase() : 'неизвестного типа';
  };

  // Рассчет стоимости заполнения бассейна
  // Средняя стоимость воды в России
  const waterCostPerCubicMeter = 40; // руб/м³
  const fillingCost = results.netVolume * waterCostPerCubicMeter;

  // Рассчет месячной стоимости обслуживания
  const monthlyCost = results.maintenanceCostAnnual / (formData.poolLocation === 'indoor' ? 12 : parseFloat(formData.seasonLength) || 3);

  return (
    <ToolResult title="Результаты расчета объема бассейна">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Объем воды */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Droplet className="w-4 h-4"/>
            Объем воды в бассейне
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.netVolume, 2)} м³
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              ({formatNumber(results.volumeLiters, 0)} литров)
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Объем воды для бассейна {formatPoolTypeName(results.poolType)} при заполнении {results.waterLevel}%
          </p>
        </div>

        {/* Годовое обслуживание */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Стоимость обслуживания
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatCurrency(results.maintenanceCostAnnual, 0)}
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              в год
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            {formatCurrency(monthlyCost, 0)} в месяц в течение сезона
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Вес воды" 
          value={`${formatNumber(results.waterWeight, 0)} кг`} 
          color="blue" 
          icon={<Waves className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Площадь поверхности" 
          value={`${formatNumber(results.surfaceArea, 2)} м² (${formatNumber(results.surfaceAreaFt, 2)} фт²)`} 
          color="purple" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Площадь облицовки" 
          value={`${formatNumber(results.liningArea, 2)} м²`} 
          icon={<FileText className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Стоимость заполнения" 
          value={formatCurrency(fillingCost, 0)} 
          detail={`при тарифе ${waterCostPerCubicMeter} руб/м³`}
          color="green"
          icon={<ShowerHead className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Потери на испарение" 
          value={`${formatNumber(results.evaporationLoss, 1)} м³/год`} 
          color="orange" 
          icon={<Wind className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Годовой расход воды" 
          value={`${formatNumber(results.waterConsumptionAnnual, 1)} м³`} 
          color="cyan" 
          icon={<Droplet className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Относительные затраты на подогрев" 
          value={`${formatNumber(results.heatingCosts, 1)} условных единиц`} 
          color="red" 
          icon={<Thermometer className="w-4 h-4"/>} 
        />

        {/* Информация о фильтрации */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mt-4 border border-blue-200 dark:border-blue-700">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Рекомендации по системе фильтрации</h4>
          <div className="space-y-2 text-sm text-blue-600 dark:text-blue-400">
            <div className="flex justify-between">
              <span>Рекомендуемый проток воды:</span>
              <span className="font-medium">{formatNumber(results.filterRecommendation.circulationRate, 1)} м³/час</span>
            </div>
            <div className="flex justify-between">
              <span>Площадь фильтра:</span>
              <span className="font-medium">{formatNumber(results.filterRecommendation.filterSize, 2)} м²</span>
            </div>
            <div className="flex justify-between">
              <span>Рекомендуемая мощность насоса:</span>
              <span className="font-medium">{formatNumber(results.filterRecommendation.pumpPower, 1)} кВт</span>
            </div>
          </div>
        </div>

        {/* Информация о химии */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mt-2 border border-green-200 dark:border-green-700">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Потребность в химических препаратах</h4>
          <div className="space-y-2 text-sm text-green-600 dark:text-green-400">
            <div className="flex justify-between">
              <span>Хлор (еженедельно):</span>
              <span className="font-medium">{formatNumber(results.chemicalRecommendation.chlorineWeekly, 2)} кг</span>
            </div>
            <div className="flex justify-between">
              <span>Регулятор pH (ежемесячно):</span>
              <span className="font-medium">{formatNumber(results.chemicalRecommendation.phRegulatorMonthly, 2)} кг</span>
            </div>
            <div className="flex justify-between">
              <span>Альгицид (ежемесячно):</span>
              <span className="font-medium">{formatNumber(results.chemicalRecommendation.algaecideMonthly, 2)} л</span>
            </div>
            <div className="flex justify-between">
              <span>Шоковый хлор (ежемесячно):</span>
              <span className="font-medium">{formatNumber(results.chemicalRecommendation.shockChlorineMonthly, 2)} кг</span>
            </div>
            <div className="flex justify-between">
              <span>Стабилизатор (единоразово):</span>
              <span className="font-medium">{formatNumber(results.chemicalRecommendation.stabilizer, 2)} кг</span>
            </div>
          </div>
        </div>
      </div>

      {/* Дополнительная информация о расчете */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
        <p className="mb-2">
          Расчет выполнен для бассейна типа "{poolTypes.find(pt => pt.id === results.poolType)?.name || 'Неизвестный'}" с уровнем заполнения {results.waterLevel}%.
        </p>
        <p>
          Обратите внимание, что приведенные данные являются расчетными и могут отличаться от фактических значений в зависимости от особенностей конструкции, материалов и условий эксплуатации бассейна.
        </p>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;